import { ProductType } from "@repo/api";
import NiceModal from "@ebay/nice-modal-react";
import { readEnv } from "@repo/helpers";
import { setPreselectedContribution, useAppDispatch, usePensionNovelBanner } from "@repo/logic";
import { PensionNovelBanner } from "@repo/ui";
import { navigate } from "gatsby";
import { type FC, useMemo } from "react";
import { contractLink } from "../../functions";
import { useContracts, usePensionDetail } from "../../queryHooks";
import { useDrawer } from "../Drawer/context";
import PensionCalculatorModal from "../PensionCalculator/Modal";

const isNewContributionChangeEnabled =
    readEnv("GATSBY_FEATURE_IS_NEW_DPS_CONTRIBUTION_CHANGE_ENABLED") === "true";

export const PensionNovelBannerWrapper: FC = () => {
    const contractsQuery = useContracts();
    const dispatch = useAppDispatch();

    // Derive UF contract ID from contracts list to fetch pension detail (for birthDate).
    // No ContractIDProvider on the contracts listing page.
    const ufContract = useMemo(
        () => contractsQuery.data?.find((c) => c.type === ProductType.Uf && !c.isChildrenContract),
        [contractsQuery.data],
    );
    const ufContractId = ufContract?.id ?? "";

    const pensionDetailQuery = usePensionDetail({
        id: ufContractId,
        type: ProductType.Uf,
        options: { enabled: Boolean(ufContractId) },
    });

    const { contract, monthlyContribution } = usePensionNovelBanner(contractsQuery, pensionDetailQuery);
    const { showDrawer, setDrawerContent, hideDrawer } = useDrawer();
    if (!contract) return null;

    const changeContributionUrl = contractLink(contract.id, contract.type, "smlouva");

    const handleChangeContribution = (newContribution?: number): void => {
        hideDrawer();
        if (isNewContributionChangeEnabled) {
            if (newContribution !== undefined) {
                dispatch(setPreselectedContribution(newContribution));
            }
            NiceModal.show("Nicemodal-ChangePensionContributionStepperModal", {
                additionalContentProps: {
                    contractId: contract.id,
                    contractType: contract.type,
                },
            });
        } else {
            navigate(changeContributionUrl);
        }
    };

    const handleButtonClick = (): void => {
        showDrawer();
        setDrawerContent(
            <PensionCalculatorModal
                onChangeContribution={handleChangeContribution}
                monthlyContribution={monthlyContribution}
            />,
        );
    };

    return <PensionNovelBanner onButtonClick={handleButtonClick} />;
};
